import React, { useEffect, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import dynamic from "next/dynamic";
import { useSelector } from "react-redux";

import { useStrainPageContext } from "context/StrainPageContext";
import { Strain } from "custom-types/Strain";
import { useEventTracker } from "hooks/useEventTracker";
import { getUserId } from "redux/selectors/user";
import { Action, Category } from "utils/trackEvent";

import Button from "components/botanic/Button";

const GeneratedStrainDescription = dynamic(
  () => import("components/StrainPage/GeneratedStrainDescription")
);

const StrainDescription = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const {
    strain: { description, name, slug, effects, flavors },
    strain,
  } = useStrainPageContext();

  const ref = useRef<HTMLDivElement | null>(null);
  const { publishEvent } = useEventTracker();
  const userId = useSelector(getUserId);

  const handleAnchorClick = () => {
    publishEvent({
      action: Action.click,
      category: Category.strainDescriptionLink,
      strainId: strain.id,
      userId: userId,
    });
  };

  useEffect(() => {
    if (ref.current) {
      const elements = ref.current.getElementsByTagName("a");
      for (let i = 0; i < elements.length; i++) {
        elements[i].addEventListener("click", handleAnchorClick);
      }
    }
    return () => {
      if (ref.current) {
        const elements = ref.current.getElementsByTagName("a");
        for (let i = 0; i < elements.length; i++) {
          elements[i].removeEventListener("click", handleAnchorClick);
        }
      }
    };
  }, [ref, handleAnchorClick]);

  if (isEmpty(strain)) {
    return <></>;
  }

  const hasNoEffects = !effects || Object.keys(effects).length < 1;
  const hasNoFlavors = !flavors || Object.keys(flavors).length < 1;
  const showReviewCta = !description || (hasNoEffects && hasNoFlavors);

  const strRegExp = new RegExp("<a ", "g");

  const displayDescription = description
    ?.replace(name, "<b>" + name + "</b>")
    .replace(strRegExp, `<a target='_blank'`);

  return (
    <div className={className} ref={ref}>
      <div itemProp="description" className="mt-lg mb-xxl green-links">
        {displayDescription ? (
          <div dangerouslySetInnerHTML={{ __html: displayDescription }} />
        ) : (
          <GeneratedStrainDescription strain={strain as Strain} />
        )}
      </div>
      {showReviewCta && (
        <Button
          variant="tertiary"
          className="text-sm mb-xl"
          href={`/review/${slug}`}
        >
          write a review
        </Button>
      )}
      <style jsx global>{`
        .green-links a {
          color: var(--color-green);
        }
      `}</style>
    </div>
  );
};

export default StrainDescription;
